import React, { useEffect, useState } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
// ... other asset imports
import googlePlayLogo from "../../assets/google-play.png";
import appStoreLogo from "../../assets/ios-app-store.png";
import fitojaLogo from "../../assets/fitoja-logo.png";
import "../blog-details/blog-details.scss";

const BlogDetails = () => {
  const { id: urlSlug } = useParams(); // Rename 'id' to 'urlSlug' for clarity
  const location = useLocation();

  const blogFromState = location.state?.blog;

  // State to hold the final blog data
  const [blog, setBlog] = useState(blogFromState || null);
  
  // isLoading is TRUE if we lack state data and need to fetch
  const [isLoading, setIsLoading] = useState(!blogFromState);
  
  // Tracks if the data loading process has successfully completed or failed
  const [isDataLoaded, setIsDataLoaded] = useState(!!blogFromState); 

  useEffect(() => {
    let isMounted = true;
    console.log("isDataLoaded: ",isDataLoaded);
    console.log("urlSlug: ",urlSlug);
    
    // Condition to fetch: Only run if the data hasn't been loaded yet AND we have a slug
    if (!isDataLoaded && urlSlug) { 
      
      const fetchBlog = async () => {
        try {
          if (isMounted) setIsLoading(true);
          
          // ⚠️ KEY FIX: We use the URL slug to fetch. 
          // This requires your backend to be updated to look up by slug/title.
          const API_ENDPOINT = `https://api.fitoja.in/api/blogs-slug/${urlSlug}`;
          console.log("Attempting to fetch blog by slug:", API_ENDPOINT); 
          
          const response = await axios.get(API_ENDPOINT);
          
          if (isMounted) {
            setBlog(response.data.data); 
          }
          
        } catch (err) {
          console.error("Failed to fetch blog (API Error or 404):", err.response?.status, err.message);
          // Set blog to null on failure so the final check redirects
          if (isMounted) {
            setBlog(null); 
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
            setIsDataLoaded(true); // Mark the process as complete
          }
        }
      };

      fetchBlog();
    }
    
    return () => {
      isMounted = false;
    };
    
    // Dependencies: Only re-run if urlSlug changes or the data process needs to start
  }, [urlSlug, isDataLoaded]); 

  
  // ------------------------- RENDERING LOGIC -------------------------
  
  // 1. Show Loading Screen: If currently fetching or waiting for fetch to start
  if (isLoading || (urlSlug && !isDataLoaded)) {
    return <div className="blog-details-loading">Loading blog...</div>;
  }
  
  // 2. Redirect on Failure: If the loading process has finished (isDataLoaded is true) 
  // AND blog is still null (meaning fetch failed or slug was invalid).
  if (!blog && isDataLoaded) {
    console.log(`REDIRECT: Blog data not found after fetch attempt for slug: ${urlSlug}`);
    return <Navigate to="/blogs" replace />;
  }
  
  // 3. Render Content: If we reach here, we have the 'blog' object.
  const plainTextContent = blog.content
    .replace(/<[^>]+>/g, "")
    .substring(0, 160);

  return (
    <div className="blog-details">
    {/* Left Side Banner */}
    <div className="blog-details__side-banner">
      <div className="banner">
        <img src={fitojaLogo} alt="Fitoja Logo" />
        <p>Download the Fitoja App</p>
        <div className="download-links">
          <a
            href="https://play.google.com/store/apps/details?id=com.fitoja.fit"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={googlePlayLogo} alt="Google Play Store" />
          </a>
          <a
            href="https://apps.apple.com/in/app/fitoja/id6740138984"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={appStoreLogo} alt="Apple App Store" />
          </a>
        </div>
      </div>
    </div>

    {/* Main Content */}
    <div className="blog-details__content">
      <Helmet>
        <title>{blog.title} | Fitoja</title>
        <meta name="description" content={plainTextContent} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={plainTextContent} />
        <meta property="og:image" content={blog.imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <img
        src={blog.imageUrl}
        alt={blog.title}
        className="blog-details__image"
        loading="lazy"
      />

      <div className="blog-details__content">
        <h1 className="blog-details__title">{blog.title}</h1>
        <div
          className="blog-details__body"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
        <Link to="/blogs" className="blog-details__content__back">
          ← Back to Health Blogs
        </Link>
      </div>
    </div>

    {/* Right Side Banner */}
    <div className="blog-details__side-banner">
      <div className="banner">
        <img src={fitojaLogo} alt="Fitoja Logo" />
        <p>Join our health community!</p>
        <div className="download-links">
          <a
            href="https://play.google.com/store/apps/details?id=com.fitoja.fit"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={googlePlayLogo} alt="Google Play Store" />
          </a>
          <a
            href="https://apps.apple.com/in/app/fitoja/id6740138984"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={appStoreLogo} alt="Apple App Store" />
          </a>
        </div>
      </div>
    </div>
  </div>
  );
};

export default BlogDetails;